import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Users, UsersRound, VolumeX, Volume2 } from 'lucide-react';

// Swedish body-doubling encouragement phrases (spoken by the pet)
const PHRASES = [
  'Jag är här bredvid dig ✨',
  'Du gör fantastiskt ifrån dig, fortsätt i din egen takt',
  'En andning i taget — vi tar det lugnt tillsammans',
  'Jag sitter bredvid och jobbar med dig',
  'Du är inte ensam, jag är här',
  'Små steg räknas, du gör jättebra ifrån dig',
  'Lyft blicken en sekund — sen tillbaka',
  'Andas in lugnt. Andas ut långsamt. Vi är här',
  'Du behöver inte vara perfekt, bara närvarande',
  'Jag är stolt över att du försöker',
  'Känn axlarna sjunka ner en aning',
  'Dags att sträcka på ryggen — jag väntar',
  'Du räcker till precis som du är',
  'Liten paus i hjärnan? Jag finns här när du är tillbaka',
  'Ett steg till — du kan',
];

const MIN_INTERVAL_MS = 150_000; // 2.5 min
const MAX_INTERVAL_MS = 240_000; // 4 min

function pickPhrase(lastIdx) {
  let idx = Math.floor(Math.random() * PHRASES.length);
  if (idx === lastIdx) idx = (idx + 1) % PHRASES.length;
  return idx;
}

function speak(text, voiceEnabled) {
  if (!voiceEnabled) return;
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    // Pick Swedish voice if available
    const voices = window.speechSynthesis.getVoices();
    const sv = voices.find((v) => (v.lang || '').toLowerCase().startsWith('sv'));
    if (sv) u.voice = sv;
    u.lang = 'sv-SE';
    u.rate = 0.95;
    u.pitch = 1.05;
    u.volume = 0.7;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

export default function BodyDoublingMode({ pet, isRunning }) {
  const [enabled, setEnabled] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [currentPhrase, setCurrentPhrase] = useState(null);
  const [show, setShow] = useState(false);
  const lastIdxRef = useRef(-1);
  const timerRef = useRef(null);
  const hideRef = useRef(null);

  const schedule = useCallback(() => {
    const delay = MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
    timerRef.current = setTimeout(() => {
      const idx = pickPhrase(lastIdxRef.current);
      lastIdxRef.current = idx;
      const phrase = PHRASES[idx];
      setCurrentPhrase(phrase);
      setShow(true);
      speak(phrase, voiceOn);
      clearTimeout(hideRef.current);
      hideRef.current = setTimeout(() => setShow(false), 9000);
      schedule();
    }, delay);
  }, [voiceOn]);

  useEffect(() => {
    if (enabled && isRunning) {
      // Initial greeting after a few seconds so user has context
      timerRef.current = setTimeout(() => {
        const idx = pickPhrase(lastIdxRef.current);
        lastIdxRef.current = idx;
        const phrase = PHRASES[idx];
        setCurrentPhrase(phrase);
        setShow(true);
        speak(phrase, voiceOn);
        clearTimeout(hideRef.current);
        hideRef.current = setTimeout(() => setShow(false), 9000);
        schedule();
      }, 6000);
    } else {
      clearTimeout(timerRef.current);
      clearTimeout(hideRef.current);
      setShow(false);
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
      }
    }
    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(hideRef.current);
    };
  }, [enabled, isRunning, schedule, voiceOn]);

  return (
    <>
      <div className="neo-card p-3 bg-white dark:bg-[#252545] flex items-center justify-between gap-2 flex-wrap" data-testid="body-doubling">
        <div className="flex items-center gap-2">
          <UsersRound className="w-4 h-4" />
          <div>
            <p className="font-bold text-sm">Body-doubling-läge</p>
            <p className="text-[11px] opacity-70">Ditt husdjur sitter bredvid och peppar tyst med mjuka meddelanden</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setVoiceOn((v) => !v)}
            className="neo-btn p-2 bg-white"
            title={voiceOn ? 'Röst på' : 'Röst av'}
            data-testid="body-doubling-voice-toggle"
          >
            {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setEnabled((e) => !e)}
            className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${enabled ? 'bg-[#B9FBC0]' : 'bg-white'}`}
            data-testid="body-doubling-toggle"
          >
            <Users className="w-3 h-3" /> {enabled ? 'PÅ' : 'AV'}
          </button>
        </div>
      </div>

      {show && currentPhrase && (
        <div className="fixed bottom-6 right-6 z-50 max-w-xs" data-testid="body-doubling-bubble">
          <div className="neo-card p-4 bg-[#FFC8DD] border-[3px] border-black shadow-[0_6px_0px_0px_rgba(0,0,0,1)] animate-[sparkle-burst_0.3s_ease-out]">
            <div className="flex items-start gap-2">
              <span className="text-2xl">{pet?.emoji || '🐾'}</span>
              <p className="text-sm font-semibold">{currentPhrase}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
